import React from "react";
import {
  Armchair,
  CalendarClock,
  FlagTriangleRight,
  Users,
} from "lucide-react";
import VehicleDetailColorIcon from "../icons/vehicle-detail-color";
import VehicleDetailInteriorColorIcon from "../icons/vehicle-detail-interior-color";
import VehicleDetailGearboxIcon from "../icons/vehicle-detail-gearbox";
import VehicleDetailFuelIcon from "../icons/vehicle-detail-fuel";
import VehicleDetailPowerIcon from "../icons/vehicle-detail-power";
import VehicleDetailMileageIcon from "../icons/vehicle-detail-mileage";
import VehicleDetailDurationIcon from "../icons/vehicle-detail-duration";
import VehicleDetailDownPaymentIcon from "../icons/vehicle-detail-down-payment";
import VehicleDetailTransferCostIcon from "../icons/vehicle-detail-transfer-cost";
import VehicleDetailLeasingRateIcon from "../icons/vehicle-detail-leasing-rate";
import VehicleDetailLeafIcon from "../icons/vehicle-detail-leaf";
import VehicleDetailZustandIcon from "../icons/vehicle-detail-zustand";
import VehicleDetailGarantieIcon from "../icons/vehicle-detail-garantie";
import VehicleDetailMaintenanceInspectionIcon from "../icons/vehicle-detail-maintenance-inspection";
import { useBrand } from "@/app/components/theme/brand-theme-boundary";
import { cn } from "@/lib/utils";

const LUCIDE_DETAIL_STROKE_WIDTH = 1.6;

const DetailCalendarClockIcon: React.FC<{ className?: string }> = ({
  className,
}) => (
  <CalendarClock
    className={className}
    strokeWidth={LUCIDE_DETAIL_STROKE_WIDTH}
  />
);

const DetailUsersIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Users className={className} strokeWidth={LUCIDE_DETAIL_STROKE_WIDTH} />
);

const DetailArmchairIcon: React.FC<{ className?: string }> = ({
  className,
}) => (
  <Armchair className={className} strokeWidth={LUCIDE_DETAIL_STROKE_WIDTH} />
);

const DetailFlagTriangleRightIcon: React.FC<{ className?: string }> = ({
  className,
}) => (
  <FlagTriangleRight
    className={className}
    strokeWidth={LUCIDE_DETAIL_STROKE_WIDTH}
  />
);

// Type for all supported vehicle detail types
export type VehicleDetailTypeT =
  | "color"
  | "interiorColor"
  | "gearbox"
  | "fuel"
  | "power"
  | "mileage"
  | "duration"
  | "downPayment"
  | "specialPayment"
  | "transferCost"
  | "leasingRate"
  | "finanzierungsRate"
  | "anschaffungspreis"
  | "secondaryFuel"
  // New types for Gebrauchtwagen
  | "warranty"
  | "condition"
  | "totalMileage"
  | "price"
  | "maintenanceInspection"
  | "firstRegistration"
  | "previousOwners"
  | "seats"
  | "businessExport";

// Type for a single detail entry
export type VehicleDetailT = {
  type: VehicleDetailTypeT;
  value: string;
  label?: string;
};

// Mapping for icon and label per detail type
const detailTypeMap: Record<
  VehicleDetailTypeT,
  { label: string; Icon: React.FC<{ className?: string }> }
> = {
  color: { label: "Farbe", Icon: VehicleDetailColorIcon },
  interiorColor: { label: "Innenfarbe", Icon: VehicleDetailInteriorColorIcon },
  gearbox: { label: "Getriebe", Icon: VehicleDetailGearboxIcon },
  fuel: { label: "Kraftstoff", Icon: VehicleDetailFuelIcon },
  secondaryFuel: {
    label: "Anderer Energieträger",
    Icon: VehicleDetailLeafIcon,
  },
  power: { label: "Leistung", Icon: VehicleDetailPowerIcon },
  mileage: { label: "Jährliche Fahrleistung", Icon: VehicleDetailMileageIcon },
  duration: { label: "Laufzeit", Icon: VehicleDetailDurationIcon },
  downPayment: { label: "Anzahlung", Icon: VehicleDetailDownPaymentIcon },
  specialPayment: {
    label: "Sonderzahlung",
    Icon: VehicleDetailDownPaymentIcon,
  },
  transferCost: {
    label: "Überführungskosten",
    Icon: VehicleDetailTransferCostIcon,
  },
  leasingRate: {
    label: "mtl. Leasingrate",
    Icon: VehicleDetailLeasingRateIcon,
  },
  finanzierungsRate: {
    label: "mtl. Finanzierungsrate",
    Icon: VehicleDetailLeasingRateIcon,
  },
  anschaffungspreis: {
    label: "Anschaffungspreis",
    Icon: VehicleDetailTransferCostIcon,
  },
  // New types mappings
  warranty: { label: "Garantie", Icon: VehicleDetailGarantieIcon },
  condition: { label: "Zustand", Icon: VehicleDetailZustandIcon },
  totalMileage: { label: "Kilometer", Icon: VehicleDetailMileageIcon },
  price: { label: "Preis", Icon: VehicleDetailLeasingRateIcon },
  maintenanceInspection: {
    label: "Wartung & Inspektion",
    Icon: VehicleDetailMaintenanceInspectionIcon,
  },
  firstRegistration: {
    label: "Erstzulassung",
    Icon: DetailCalendarClockIcon,
  },
  previousOwners: {
    label: "Vorbesitzer",
    Icon: DetailUsersIcon,
  },
  seats: {
    label: "Sitze",
    Icon: DetailArmchairIcon,
  },
  businessExport: {
    label: "Gewerbe, Export",
    Icon: DetailFlagTriangleRightIcon,
  },
};

/**
 * VehicleDetailsList
 *
 * Renders a list of vehicle details (icon, label, value) in a consistent layout.
 * Icons and labels are mapped automatically by type.
 *
 * @param details Array of detail objects (type, value)
 */
const VehicleDetailsList: React.FC<{ details: VehicleDetailT[] }> = ({
  details,
}) => {
  const { isAudi } = useBrand();

  if (details.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "divide-border divide-y rounded-lg bg-white lg:grid lg:grid-cols-3 lg:gap-x-4",
        isAudi && "bg-background rounded-[20px] text-white"
      )}
    >
      {details.map((detail) => {
        const { label: defaultLabel, Icon } = detailTypeMap[detail.type];
        const label = detail.label ?? defaultLabel;
        return (
          <div
            key={detail.type}
            className={cn(
              "flex flex-row items-center gap-4 px-1 py-3 first:pt-4 last:pb-4 lg:last:border-b",
              isAudi && "lg:last:border-border"
            )}
          >
            <Icon
              className={cn(
                "text-primary h-7 min-h-[28px] w-7 min-w-[28px] shrink-0",
                isAudi && "text-muted-foreground"
              )}
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <div
                className={cn(
                  "p-0 text-gray-700",
                  isAudi && "text-muted-foreground"
                )}
              >
                {label}:
              </div>
              <div
                className={cn(
                  "p-0 font-semibold break-words text-gray-900",
                  isAudi && "text-white"
                )}
              >
                {detail.value}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VehicleDetailsList;
